import React, { useMemo } from 'react'
import { RouteProps, useSearchParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import ResetPasswordTemplate, { ResetPasswordTemplateProps } from '../../templates/Password/reset'
import { useFooter } from '../../relay/Footer/useFooter'
import { useHeader } from '../../relay/Header/useHeader'
import { actions, selectors } from '../../redux'

const ResetPasswordPage: React.FC<RouteProps> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const header = useHeader()
  const footer = useFooter()
  const [searchParams] = useSearchParams()
  const reset = useSelector(selectors.auth.reset)

  const templateProps: ResetPasswordTemplateProps = useMemo(
    () => ({
      layout: {
        header,
        footer,
      },
      resetPasswordForm: {
        background: `${import.meta.env.BASE_URL}images/onboarding/pickCCI.jpg`,
        header: {
          title: t('password.reset.header.title'),
        },
        content: {
          title: t('password.reset.content.title'),
          formikForm: {
            initialValues: {
              password: '',
              confirmPassword: '',
            },
            validationSchema: Yup.object().shape({
              password: Yup.string().required(),
              confirmPassword: Yup.string().oneOf(
                [Yup.ref('password')],
                t('form.error.samePassword')
              ),
            }),
            onSubmit: (values) => {
              dispatch(
                actions.auth.resetRequest({
                  token: searchParams.get('token') || '',
                  email: searchParams.get('email') || '',
                  password: values.password,
                })
              )
            },
            validateOnChange: false,
          },
          fields: {
            password: {
              name: 'password',
              label: t('password.reset.password.label'),
              placeholder: t('password.reset.password.placeholder'),
              required: true,
            },
            confirmPassword: {
              name: 'confirmPassword',
              label: t('password.reset.confirmPassword.label'),
              placeholder: t('password.reset.confirmPassword.placeholder'),
              required: true,
            },
          },
          errors: reset.errors,
          submitButton: {
            text: t('password.reset.submit.label'),
            isPending: reset.pending,
            isSuccess: reset.success,
            pendingText: t('password.reset.submit.pending'),
            successText: t('password.reset.submit.success'),
          },
        },
      },
    }),
    [dispatch, footer, header, reset.errors, reset.pending, reset.success, searchParams, t]
  )

  return <ResetPasswordTemplate {...templateProps} />
}

export default ResetPasswordPage
